import type { SelectedDoc } from "@/pages/ClientProfile/types";
import { Paper, Button, Container } from "@mantine/core";
import { IconX, IconArrowLeft } from "@tabler/icons-react";
import TaxInfoDocPreview from "../TaxInfoDocPreview";
import { useState, useEffect } from "react";
import { isMobileDevice } from "@utils/deviceDetection";
import "./DocumentViewerPanel.scss";

interface DocumentViewerPanelProps {
  doc: SelectedDoc;
  onClose: () => void;
}
/**
 * DocumentViewerPanel component renders a document within a styled panel.
 * It provides a close button to exit the viewer and utilizes the
 * TaxInfoDocPreview component to display the document content.
 *
 * Props:
 * - doc: The selected document to be viewed.
 * - onClose: A function to call when the close button is clicked.
 * - onClearDocument: A function to call to clear the document from the view.
 *
 * Returns a JSX element that represents the document viewer panel.
 */

export const DocumentViewerPanel = ({
  doc,
  onClose,
}: DocumentViewerPanelProps): JSX.Element => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Mobile full-screen view
  if (isMobile) {
    const handleMobileBack = () => {
      // On mobile, just close directly - no confirmation needed
      onClose();
    };

    return (
      <div className="document-viewer-mobile-fullscreen">
        <header className="mobile-header">
          <button
            className="back-button"
            onClick={handleMobileBack}
            aria-label="Go back"
          >
            <IconArrowLeft size={24} />
          </button>
          <h2 className="mobile-title">{doc.label || "Document"}</h2>
        </header>

        <main className="mobile-content">
          <TaxInfoDocPreview selectedDoc={doc} />
        </main>
      </div>
    );
  }

  // Desktop panel view
  return (
    <Paper
      withBorder
      shadow="sm"
      style={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
      p={20}
      mt={"45"}
    >
      <Button
        variant="subtle"
        size="sm"
        onClick={onClose}
        style={{
          position: "absolute",
          right: 8,
          top: 8,
          zIndex: 1,
        }}
        aria-label="Close document viewer"
      >
        <IconX size={16} />
      </Button>
      <Container mt={"lg"}>
        <TaxInfoDocPreview selectedDoc={doc} />
      </Container>
    </Paper>
  );
};
